<script setup lang="ts">
// The three General Sans weights every page uses above the fold. Without
// preloads the browser only discovers them after parsing the stylesheet, so
// the first paint renders text-less (the nav pill flashes empty before the
// Sign in label appears). `?url` keeps the Vite-hashed asset path correct in
// both dev and prod. They must be the same files the DS sheet's @font-face
// rules load, or the preload fetches a font nothing uses.
import generalSansMedium from '~ds/assets/fonts/general_sans/GeneralSans-Medium.woff2?url';
import generalSansRegular from '~ds/assets/fonts/general_sans/GeneralSans-Regular.woff2?url';
import generalSansSemibold from '~ds/assets/fonts/general_sans/GeneralSans-Semibold.woff2?url';

const { toasts, close } = useToast();

// Icon/manifest <link> hrefs must point at this cluster's path prefix
// (e.g. /au/favicon.ico), not the apex — on a path-routed deploy the apex is a
// separate app. Build assets are handled by app.cdnURL and og:image by the SEO
// module (via site.url); the SEO module ignores <link> tags, so they're prefixed
// here. pathPrefix is identical on server and client (hydrated cluster state),
// so the hrefs stay hydration-stable.
const { pathPrefix } = useCurrentCluster();
const clusterAsset = (path: string): string => `${pathPrefix.value}${path}`;

// Local dev only: when served from a worktree fleet slot, prefix the tab title with
// [WTX] so parallel worktrees are tellable apart in the browser tab strip. Matches
// every Phoenix host a slot serves — the apex (wtN.anyway.local[host]), the test
// cluster (wtN-cluster.…) and the schools domain (schools.wtN.…) — via the shared
// `wtN` token followed by `.` or `-`. The pattern only ever matches local hosts (prod
// is anyway.ai), so it's inherently local-only. The host is identical on SSR
// (x-forwarded-host) and client (window.location.host), so the title stays
// hydration-stable. When there's no slot the head object omits titleTemplate entirely,
// leaving the SEO module's default (%s %separator %siteName) untouched; when there is,
// we reuse those same params so the "| Anyway" suffix is preserved and we only prefix.
const worktreeSlot = useRequestHost()?.match(/(?:^|\.)wt(\d+)[.-]/i)?.[1];

useHead({
  ...(worktreeSlot ? { titleTemplate: `[WT${worktreeSlot}] %s %separator %siteName` } : {}),
  link: computed(() => [
    ...[generalSansRegular, generalSansMedium, generalSansSemibold].map(href => ({
      rel: 'preload',
      as: 'font',
      type: 'font/woff2',
      href,
      crossorigin: '',
    } as const)),
    { rel: 'icon', type: 'image/x-icon', href: clusterAsset('/favicon.ico') },
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: clusterAsset('/favicon-32x32.png') },
    { rel: 'icon', type: 'image/png', sizes: '16x16', href: clusterAsset('/favicon-16x16.png') },
    { rel: 'apple-touch-icon', sizes: '180x180', href: clusterAsset('/apple-touch-icon.png') },
    { rel: 'manifest', href: clusterAsset('/site.webmanifest') },
  ]),
});
</script>

<template>
  <KUICoreTooltipProvider>
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <CoreSessionUnAuthedModal />
    <SchoolsAccountSwitcherLoader />
  </KUICoreTooltipProvider>
  <Teleport to="body">
    <KUICoreToastViewport :toasts="toasts" @close="close" />
  </Teleport>
</template>
